import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import ControlledInput from "../../components/input/controlled-input";
import { Controller, useForm } from "react-hook-form";
import MuiPhone from "../../components/input/mui-phone-input";
import { yupResolver } from "@hookform/resolvers/yup";
import { DealershipInformationSchema } from "../../schemas/dealership-information.schema";
import { areObjectsEqual } from "../../utils/utils.service";
import { useSettingsReadOnly } from "../../contexts/SettingsReadOnlyContext";

type Inputs =
  | "name"
  | "phoneNumber"
  | "email"
  | "address"
  | "inventoryLink"
  | "financeLink"
  | "website"
  | "googleLink"
  | "yelpLink";

interface FormInputs {
  name: string;
  phoneNumber: string;
  email?: string;
  address: string;
  inventoryLink?: string;
  financeLink?: string;
  website: string;
  googleLink?: string;
  yelpLink?: string;
}

const inputs = [
  { name: "name", label: "Dealership Name *", placeholder: "Enter dealership name" },
  { name: "phoneNumber", label: "Phone Number *", placeholder: "Enter phone number" },
  { name: "email", label: "Dealership Email", placeholder: "Enter email" },
  { name: "address", label: "Dealership Address *", placeholder: "Enter address" },
  { name: "inventoryLink", label: "Inventory URL", placeholder: "Enter inventory URL" },
  { name: "financeLink", label: "Finance URL", placeholder: "Enter finance URL" },
  { name: "website", label: "Website *", placeholder: "Enter website" },
  {
    name: "googleLink",
    label: "Google Business Profile URL",
    placeholder: "Enter google business profile URL",
  },
  { name: "yelpLink", label: "Yelp Profile URL", placeholder: "Enter yelp URL" },
];

const DealershipInformation = ({
  state,
  setState,
  dealerShipDefaultValues,
  oBlockChecker,
}: any) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useForm<FormInputs>({
    defaultValues: useMemo(() => {
      return {
        name: state?.dealerName,
        phoneNumber: state?.dealerPhone,
        email: state?.dealerEmail,
        address: state?.dealerAddress,
        website: state?.website,
        googleLink: state?.googleLink,
        yelpLink: state?.yelpLink,
        inventoryLink: state?.inventoryLink,
        financeLink: state?.financeLink,
      };
    }, [
      state?.dealerAddress,
      state?.dealerEmail,
      state?.financeLink,
      state?.googleLink,
      state?.inventoryLink,
      state?.dealerName,
      state?.dealerPhone,
      state?.website,
      state?.yelpLink,
    ]),
    mode: "onChange",
    resolver: yupResolver(DealershipInformationSchema),
  });

  const readOnly = useSettingsReadOnly();

  useEffect(() => {
    const subscription = watch(
      ({
        name,
        phoneNumber: phone_number,
        email,
        address,
        website,
        googleLink,
        yelpLink,
        inventoryLink,
        financeLink,
      }: any) => {
        // Update state based on watched values

        setState((prevState: any) => {
          const newState = {
            ...prevState,
            dealerName: name,
            dealerPhone: phone_number,
            dealerEmail: email,
            dealerAddress: address,
            website,
            googleLink: googleLink,
            yelpLink: yelpLink,
            inventoryLink: inventoryLink,
            financeLink: financeLink,
          };

          const isEqual = areObjectsEqual(dealerShipDefaultValues, newState);
          oBlockChecker(!isEqual);
          return newState; // Return the new state
        });
      }
    );
    return () => subscription.unsubscribe();
  }, [dealerShipDefaultValues, oBlockChecker, setState, state, watch]);

  return (
    <Box gap={"24px"} display={"flex"} flexDirection={"column"}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography sx={{ fontSize: "20px", fontWeight: "600" }}>Dealer Information</Typography>
        <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#737373" }}>
          Manage your dealership details and team member access.
        </Typography>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2}>
          {inputs.map((inputItem) => (
            <Grid item key={inputItem.name} xs={12} md={6} lg={4}>
              <Box width="100%">
                <Controller
                  control={control}
                  name={inputItem.name as Inputs}
                  render={({ field }) =>
                    inputItem.name === "phoneNumber" ? (
                      <MuiPhone
                        label={"Phone Number *"}
                        id="phoneNumber"
                        placeholder="Enter your phone number"
                        error={errors.phoneNumber && errors.phoneNumber.message}
                        {...field}
                        disabled={readOnly}
                      />
                    ) : (
                      <ControlledInput
                        error={errors[inputItem.name as keyof FormInputs]?.message}
                        id={inputItem.name}
                        label={inputItem.label}
                        placeholder={inputItem.placeholder}
                        type="text"
                        {...field}
                        disabled={readOnly}
                      />
                    )
                  }
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default DealershipInformation;
